import { useContext, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  ACTION_NAME_ABSENT,
  ACTION_NAME_DROP_OFF,
  ACTION_NAME_IN_PLACE,
  ACTION_NAME_PICK_UP,
  ACTION_NAME_READY_TO_LEAVE,
} from '../../../constants.ts';
import { SelectedVehicleContext } from '../../../contexts/vehicle/SelectedVehicleContext.ts';
import { postActions } from '../../../queries/postActions.ts';
import { Booking } from '../../../schema/Booking.ts';
import { Place } from '../../../schema/Place.ts';
import { useTerritory } from '../../../services/Territory.ts';
import { useShiftByVehicle } from '../../../services/Vehicle.ts';
import {
  displayInPlaceButton,
  displayReadyToLeaveButton,
  enableDropOffButton,
  enablePickUpButton,
  getBookingsToDropOff,
  getBookingsToPickUp,
  getCustomerIdentifier,
  isDropOffButtonChecked,
  isPickUpButtonChecked,
  shouldCustomerIdentifierBeCrossedOut,
} from '../../../utils/roadmapUtils.ts';
import { FormatTime } from '../../../utils/utils.ts';
import { LoadingButton } from '../../ui/Button.tsx';
import { CustomSpinner } from '../../ui/Spinner.tsx';

interface Props {
  currentPlace: undefined | Place;
  selectedPlace: Place | null;
  nextPlace: undefined | Place;
  showPlaceDetails: boolean;
  handleClosePlaceDetails: () => void;
}
export function RoadmapActions(props: Readonly<Props>) {
  const params = useParams() as { territorySlug: string };
  const { t } = useTranslation();
  const { selectedVehicle } = useContext(SelectedVehicleContext);
  const { data: shiftData } = useShiftByVehicle(params.territorySlug, selectedVehicle?.id || 0);
  const [isInPlaceButtonLoading, setIsInPlaceButtonLoading] = useState<boolean>(false);
  const [isReadyToLeaveButtonLoading, setIsReadyToLeaveButtonLoading] = useState<boolean>(false);
  const [isPickUpButtonLoading, setIsPickUpButtonLoading] = useState<boolean>(false);
  const [isDropOffButtonLoading, setIsDropOffButtonLoading] = useState<boolean>(false);
  const { data: territory } = useTerritory(params.territorySlug);
  const handleClosePlaceDetails = () => props.handleClosePlaceDetails();
  async function handleInPlaceButtonClick(rideId: number | undefined, placeId: number | undefined) {
    if (placeId === undefined || rideId === undefined) {
      throw TypeError('Selected Place or Ride is null');
    }
    try {
      setIsInPlaceButtonLoading(true);
      await postActions(params.territorySlug, selectedVehicle?.id || 0, [
        {
          name: ACTION_NAME_IN_PLACE,
          kwargs: {
            ride_id: rideId,
            place_id: placeId,
          },
        },
      ]);
      toast.success(`${t('roadmap.in-place')}`);
    } catch {
      toast.error(`${t('roadmap.in-place-error')}`);
    } finally {
      setIsInPlaceButtonLoading(false);
    }
  }

  async function handleReadyToLeaveButtonClick(rideId: number | undefined, placeId: number | undefined) {
    if (placeId === undefined || rideId === undefined) {
      throw TypeError('Selected Place or Ride is null');
    }
    try {
      setIsReadyToLeaveButtonLoading(true);
      await postActions(params.territorySlug, selectedVehicle?.id || 0, [
        {
          name: ACTION_NAME_READY_TO_LEAVE,
          kwargs: {
            ride_id: rideId,
            place_id: placeId,
          },
        },
      ]);
      toast.success(`${t('roadmap.ready-to-leave')}`);
      handleClosePlaceDetails();
    } catch {
      toast.error(`${t('roadmap.ready-to-leave-error')}`);
    } finally {
      setIsReadyToLeaveButtonLoading(false);
    }
  }

  async function handlePickUpButtonClick(rideId: number, placeId: number, bookingId: number) {
    setIsPickUpButtonLoading(true);
    try {
      await postActions(params.territorySlug, selectedVehicle?.id || 0, [
        {
          name: ACTION_NAME_PICK_UP,
          kwargs: {
            ride_id: rideId,
            place_id: placeId,
            booking_id: bookingId,
          },
        },
      ]);
      toast.success(`${t('roadmap.pickup')}`);
    } catch {
      toast.error(`${t('roadmap.pickup-error')}`);
    } finally {
      setIsPickUpButtonLoading(false);
    }
  }

  async function handleAbsentButtonClick(rideId: number, placeId: number, bookingId: number) {
    setIsPickUpButtonLoading(true);
    try {
      await postActions(params.territorySlug, selectedVehicle?.id || 0, [
        {
          name: ACTION_NAME_ABSENT,
          kwargs: {
            ride_id: rideId,
            place_id: placeId,
            booking_id: bookingId,
          },
        },
      ]);
      toast.success(`${t('roadmap.absent')}`);
    } catch {
      toast.error(`${t('roadmap.absent-error')}`);
    } finally {
      setIsPickUpButtonLoading(false);
    }
  }
  async function handleDropOffButtonClick(rideId: number, placeId: number, bookingId: number) {
    setIsDropOffButtonLoading(true);
    try {
      await postActions(params.territorySlug, selectedVehicle?.id || 0, [
        {
          name: ACTION_NAME_DROP_OFF,
          kwargs: {
            ride_id: rideId,
            place_id: placeId,
            booking_id: bookingId,
          },
        },
      ]);
      toast.success(`${t('roadmap.dropoff')}`);
    } catch {
      toast.error(`${t('roadmap.dropoff-error')}`);
    } finally {
      setIsDropOffButtonLoading(false);
    }
  }
  return (
    <Modal show={props.showPlaceDetails} onHide={handleClosePlaceDetails} centered>
      <Modal.Header closeButton className={'border-0'}>
        <Modal.Title>
          <p className={'card-subtitle'}>
            {props.selectedPlace?.name} - #{props.selectedPlace?.order}
          </p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={'row'}>
          <div className={'col'}>
            <p>
              <span className={'text-bold'}>Place : </span>
              <span className={'text-basic'}>{props.selectedPlace?.id}</span>
            </p>
            <p>
              <span className={'text-bold'}>Type : </span>
              <span className={'text-basic'}>{props.selectedPlace?.type.replace(/_/g, ' ')}</span>
            </p>
            <p>
              <span className={'text-bold'}>Status : </span>
              <span className={'text-basic'}>{props.selectedPlace?.status.replace(/_/g, ' ')}</span>
            </p>
          </div>
          <div className={'col'}>
            <p>
              <span className={'text-bold'}>Departure time : </span>
              <span className={'text-basic'}>
                {props.selectedPlace ? FormatTime(props.selectedPlace?.departure_time, false) : '-'}
              </span>
            </p>
            <p>
              <span className={'text-bold'}>Arrival time : </span>
              <span className={'text-basic'}>
                {props.selectedPlace ? FormatTime(props.selectedPlace?.arrival_time, false) : '-'}
              </span>
            </p>
            <p>
              {props.selectedPlace?.min_time && (
                <>
                  <span className={'text-bold'}>Min time : </span>
                  <span className={'text-basic'}>
                    {FormatTime(props.selectedPlace?.min_time, false)}
                  </span>
                </>
              )}
            </p>
            <p>
              {props.selectedPlace?.max_time && (
                <>
                  <span className={'text-bold'}>Max time : </span>
                  <span className={'text-basic'}>
                    {FormatTime(props.selectedPlace?.max_time, false)}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
        {territory?.permissions.can_perform_roadmap_actions && props.selectedPlace !== null && (
          <div className={'row'}>
            {props.selectedPlace?.bookings.length > 0 && (
              <>
                <hr className={'basic-hr mt-4 mb-4'} />
                <div className={'col-sm-6'}>
                  <div className={'text-center mb-3'}>
                    <span className={'pickup-badge text-center'}>
                      <img
                        src={`/theme/media/arrow-up.svg`}
                        height="15"
                        className={'me-2'}
                        alt="Pickup or dropoff logo"
                      />
                      {t('roadmap.pickup-text')}
                      {isPickUpButtonLoading && (
                        <CustomSpinner class="ms-2 d-inline-block align-middle" size="sm" />
                      )}
                    </span>
                  </div>
                  {getBookingsToPickUp(props.selectedPlace)?.map((booking: Booking) => (
                    <div
                      key={booking.id}
                      className={'d-flex align-items-center justify-content-end me-5'}>
                      <p
                        className={`me-3 ${enablePickUpButton(props.selectedPlace, props.currentPlace, booking) ? 'text-basic' : 'text-disabled'} ${shouldCustomerIdentifierBeCrossedOut(booking) && 'text-line-through'}`}>
                        {getCustomerIdentifier(booking)}
                      </p>
                      <Form.Check
                        type={'checkbox'}
                        className={'action-checkbox me-3'}
                        onChange={() => {}}
                        checked={isPickUpButtonChecked(booking)}
                        disabled={
                          !enablePickUpButton(props.selectedPlace, props.currentPlace, booking) ||
                          isPickUpButtonLoading
                        }
                        onClick={() =>
                          handlePickUpButtonClick(
                            shiftData?.ride || 0,
                            props.selectedPlace?.id || 0,
                            booking.id,
                          )
                        }
                      />
                      {enablePickUpButton(props.selectedPlace, props.currentPlace, booking) && (
                        <button
                          type={'button'}
                          className={'p-0 m-O btn btn-link'}
                          onClick={() =>
                            handleAbsentButtonClick(
                              shiftData?.ride || 0,
                              props.selectedPlace?.id || 0,
                              booking.id,
                            )
                          }>
                          <img
                            src={`/theme/media/absent.svg`}
                            height="25"
                            className={'mt-1'}
                            alt="Absent logo"
                          />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className={'col-sm-6'}>
                  <div className={'text-center mb-3'}>
                    <span className={'dropoff-badge text-center'}>
                      <img
                        src={`/theme/media/arrow-down.svg`}
                        height="15"
                        className={'me-2'}
                        alt="Pickup or dropoff logo"
                      />
                      {t('roadmap.dropoff-text')}
                      {isDropOffButtonLoading && (
                        <CustomSpinner class="ms-2 d-inline-block align-middle" size="sm" />
                      )}
                    </span>
                  </div>
                  {getBookingsToDropOff(props.selectedPlace)?.map((booking: Booking) => (
                    <div
                      key={booking.id}
                      className={'d-flex align-items-center justify-content-start ms-5'}>
                      <Form.Check
                        type={'checkbox'}
                        className={'action-checkbox'}
                        onChange={() => {}}
                        checked={isDropOffButtonChecked(booking)}
                        disabled={
                          !enableDropOffButton(props.selectedPlace, props.currentPlace, booking) ||
                          isDropOffButtonLoading
                        }
                        onClick={() =>
                          handleDropOffButtonClick(
                            shiftData?.ride || 0,
                            props.selectedPlace?.id || 0,
                            booking.id,
                          )
                        }
                      />
                      <p
                        className={`ms-2 ${enableDropOffButton(props.selectedPlace, props.currentPlace, booking) ? 'text-basic' : 'text-disabled'} ${shouldCustomerIdentifierBeCrossedOut(booking) && 'text-line-through'}`}>
                        {getCustomerIdentifier(booking)}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className={'mt-3'}>
              <div className={'col'}>
                <div className={'row mt-5'}>
                  {shiftData?.ride &&
                    props.selectedPlace?.id &&
                    props.currentPlace !== undefined &&
                    props.nextPlace !== undefined && (
                      <>
                        <div className={'col-sm-6 text-sm-end'}>
                          <LoadingButton
                            type={'submit-button'}
                            text={t('roadmap.in-place-button')}
                            imagePath={null}
                            loading={isInPlaceButtonLoading}
                            onClick={() =>
                              handleInPlaceButtonClick(shiftData?.ride, props.selectedPlace?.id)
                            }
                            disabled={
                              !displayInPlaceButton(
                                props.currentPlace,
                                props.nextPlace,
                                props.selectedPlace,
                              ) || isInPlaceButtonLoading
                            }
                          />
                        </div>
                        <div className={'col-sm-6'}>
                          <LoadingButton
                            type={'submit-button'}
                            text={t('roadmap.ready-to-leave-button')}
                            imagePath={null}
                            loading={isReadyToLeaveButtonLoading}
                            onClick={() =>
                              handleReadyToLeaveButtonClick(shiftData?.ride, props.selectedPlace?.id)
                            }
                            disabled={
                              !displayReadyToLeaveButton(props.currentPlace, props.selectedPlace) ||
                              isReadyToLeaveButtonLoading
                            }
                          />
                        </div>
                      </>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
